import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Car, MapPin, Calendar, Clock, Scale, Euro, Search, 
  ArrowRight, User, Star, Filter, Plane
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { tripsApi } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { StarRating } from '../../components/StarRating';
import { toast } from 'sonner';

const SearchTripsPage = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    departure_city: '',
    arrival_city: '',
    date: '',
    min_weight: '',
    transport_mode: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.departure_city) params.departure_city = filters.departure_city;
      if (filters.arrival_city) params.arrival_city = filters.arrival_city;
      if (filters.date) params.date = filters.date;
      if (filters.min_weight) params.min_weight = parseFloat(filters.min_weight);
      if (filters.transport_mode) params.transport_mode = filters.transport_mode;
      
      const response = await tripsApi.getAll(params);
      setTrips(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTrips();
  };

  const clearFilters = () => {
    setFilters({
      departure_city: '',
      arrival_city: '',
      date: '',
      min_weight: '',
      transport_mode: '',
    });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="search-trips-page">
      <div>
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
          Trouver un trajet
        </h1>
        <p className="text-slate-600 mt-1">
          Recherchez des cotransporteurs qui font déjà le trajet
        </p>
      </div>

      {/* Search Form */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <form onSubmit={handleSearch}>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                  <Input
                    placeholder="Ville de départ"
                    value={filters.departure_city}
                    onChange={(e) => setFilters({ ...filters, departure_city: e.target.value })}
                    className="pl-10"
                    data-testid="search-departure"
                  />
                </div>
              </div>
              <div className="flex-1">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                  <Input
                    placeholder="Ville d'arrivée"
                    value={filters.arrival_city}
                    onChange={(e) => setFilters({ ...filters, arrival_city: e.target.value })}
                    className="pl-10"
                    data-testid="search-arrival"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="search-btn"
              >
                <Search className="w-5 h-5 mr-2" />
                Rechercher
              </Button>
            </div>

            {/* Advanced Filters */}
            <div className="mt-4">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="text-slate-600"
              >
                <Filter className="w-4 h-4 mr-2" />
                {showFilters ? 'Masquer les filtres' : 'Plus de filtres'}
              </Button>

              {showFilters && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-100">
                  <div className="space-y-2">
                    <Label>Date de départ</Label>
                    <Input
                      type="date"
                      value={filters.date}
                      onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                      data-testid="search-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Poids minimum (kg)</Label>
                    <Input
                      type="number"
                      placeholder="5"
                      value={filters.min_weight}
                      onChange={(e) => setFilters({ ...filters, min_weight: e.target.value })}
                      data-testid="search-weight"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mode de transport</Label>
                    <select
                      value={filters.transport_mode}
                      onChange={(e) => setFilters({ ...filters, transport_mode: e.target.value })}
                      className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                      data-testid="search-transport-mode"
                    >
                      <option value="">Tous</option>
                      <option value="road">Routier</option>
                      <option value="air">Fret aérien</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={clearFilters}
                      className="w-full"
                    >
                      Effacer les filtres
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase">
            {trips.length} trajet{trips.length > 1 ? 's' : ''} trouvé{trips.length > 1 ? 's' : ''}
          </h2>
        </div>

        {loading ? (
          <Card className="border-slate-200">
            <CardContent className="p-8 text-center">
              <div className="spinner mx-auto"></div>
              <p className="text-slate-500 mt-4">Recherche en cours...</p>
            </CardContent>
          </Card>
        ) : trips.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <Car className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">Aucun trajet trouvé</p>
              <p className="text-slate-400 text-sm mt-2">
                Essayez de modifier vos critères de recherche
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {trips.map((trip) => (
              <Card 
                key={trip.id} 
                className="border-slate-200 hover:shadow-lg hover:border-orange-200 transition-all"
                data-testid={`trip-result-${trip.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Route */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <MapPin className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-bold text-slate-900">{trip.departure_city}</p>
                            <p className="text-xs text-slate-500">{trip.departure_postal_code}</p>
                          </div>
                        </div>
                        <ArrowRight className="w-6 h-6 text-slate-400" />
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                            <MapPin className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <p className="font-bold text-slate-900">{trip.arrival_city}</p>
                            <p className="text-xs text-slate-500">{trip.arrival_postal_code}</p>
                          </div>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                        {trip.transport_mode === 'air' ? (
                          <span className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full font-medium" data-testid="transport-mode-badge">
                            <Plane className="w-4 h-4" />
                            Fret aérien
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full font-medium" data-testid="transport-mode-badge">
                            <Car className="w-4 h-4" />
                            Routier
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(trip.departure_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {trip.departure_time}
                        </span>
                        <span className="flex items-center gap-1">
                          <Scale className="w-4 h-4" />
                          Jusqu'à {trip.available_weight} kg
                        </span>
                      </div>

                      {trip.transport_mode === 'air' && (trip.airline || trip.flight_number) && (
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-1.5">
                          {trip.airline && <span>Compagnie: <strong>{trip.airline}</strong></span>}
                          {trip.flight_number && <span>Vol: <strong>{trip.flight_number}</strong></span>}
                          {trip.departure_airport && <span>De: <strong>{trip.departure_airport}</strong></span>}
                          {trip.arrival_airport && <span>Vers: <strong>{trip.arrival_airport}</strong></span>}
                        </div>
                      )}

                      {trip.description && (
                        <p className="text-sm text-slate-500 mt-2 line-clamp-2">
                          {trip.description}
                        </p>
                      )}
                    </div>

                    {/* Carrier & Price */}
                    <div className="flex md:flex-col items-center md:items-end justify-between md:justify-start gap-4 pt-4 md:pt-0 border-t md:border-t-0 md:border-l border-slate-200 md:pl-6">
                      {/* Carrier Info */}
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <span className="text-orange-600 font-bold">
                            {trip.carrier_name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{trip.carrier_name}</p>
                          {trip.carrier_rating ? (
                            <div className="flex items-center gap-1">
                              <StarRating rating={trip.carrier_rating} size="sm" />
                              <span className="text-xs text-slate-500">({trip.carrier_reviews})</span>
                            </div>
                          ) : (
                            <p className="text-xs text-slate-400">Nouveau</p>
                          )}
                        </div>
                      </div>

                      {/* Price */}
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600">
                          {trip.price_per_kg}€<span className="text-sm font-normal text-slate-500">/kg</span>
                        </p>
                        <Link to={`/dashboard/new-parcel?trip=${trip.id}`}>
                          <Button 
                            size="sm" 
                            className="mt-2 bg-orange-500 hover:bg-orange-600"
                            data-testid={`contact-btn-${trip.id}`}
                          >
                            Envoyer un colis
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchTripsPage;
